import React from 'react';
import { CommonOnUpdate, CommonOnError } from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import classnames from 'classnames';
import ErrorMessage from 'components/ErrorMessage';
import { debounce } from 'lodash';
import { federalAgencyList } from 'constants/enums/sharedOptions';
import ValidationElement from '../ValidationElement';
import Dropdown from '../Dropdown';
import { internalTesting } from '../Dropdown/Dropdown';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';

interface Props extends ValidationElementProps {
  attribute?: string
  name: string
  label?: any
  placeholder?: any
  ariaLabel?: string
  disabled?: any
  required?: boolean
  value?: string | string[]
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  className?: string
  warningMessage?: string
}

export default class FederalAgencyAutoComplete extends ValidationElement<Props, State> {
  agency: React.RefObject<typeof internalTesting.Dropdown>;

  constructor(props: Props) {
    super(props);

    this.state = {
      focus: false,
      agencies: federalAgencyList
    };

    this.agency = React.createRef();
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.onSuggestionSelected = debounce(this.onSuggestionSelected, 500, { leading: false, trailing: true });
    this.fetchSuggestions = this.fetchSuggestions.bind(this);
    this.fetchSuggestions = debounce(this.fetchSuggestions, 700, { leading: false, trailing: true });
  }

  update = (queue, agencies) => {
    this.props.onUpdate({
      name: this.props.name,
      value: this.props.value,
      focus: this.state.focus,
      ...queue,
    });
  };

  fetchSuggestions(value) {
    this.setState(() => ({ agencies: federalAgencyList.filter((x) => x.toLowerCase().includes(value.toLowerCase())) }));
  }

  /**
   * Update function when there is only one agency
   * @param {string} values - Agency string
   */
  updateSingleAgency = (values) => {
    const { value } = values;

    if (value !== this.props.value) {
      this.fetchSuggestions(value);
    }
    this.update({ value }, this.getAgencyList());
  };

  /**
   * Filters agency names match anything in the agency list
   * @param {string} item - Agency Name
   */
  filterValidInstitutions = (item) => {
    const agencies = this.getAgencyList() || [];
    return agencies.filter((x) => (x.toLowerCase() === item.toLowerCase()));
  };

  handleError = (value, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      code: `agency.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    // Determine if a `notfound` error is present and has a value of `false`.
    // When this happens we want to change the focus to the appropriate element
    // dependent on if this is singular or plural.
    const notfound = arr2.some((x) => x.valid === false && x.code.includes('notfound'));

    this.setState({ showComments: notfound });

    return this.props.onError(value, arr2);
  };

  /**
   * Handle the focus event.
   */
  handleFocus = (event) => {
    this.setState({ focus: true }, () => {
      this.props.updateAgencyFocused(this.state.focus);
      super.handleFocus(event);
    });
  };

  /**
   * Handle the blur event.
   */
  handleBlur = (event) => {
    this.setState({ focus: false }, () => {
      this.props.updateAgencyFocused(this.state.focus);
      super.handleBlur(event);
    });
  };

  validAgency(value): boolean {
    const agencies = this.getAgencyList() || [];
    return agencies.includes(value);
  }

  getAgencyList = () => this.state.agencies;

  renderOptions = () => {
    const agencies = this.getAgencyList();
    const agencyOptions = agencies.map((x, index) => (
      <option key={`${x}-${index}`} value={`${x || ''}`}>
        {`${x || ''}`}
      </option>
    ));

    if (this.props.placeholder) {
      return [
        <option key="placeholder" value="">
          {this.props.placeholder}
        </option>,
      ].concat(agencyOptions);
    }

    return agencyOptions;
  };

  appropriateValue = (value: Props['value']) => {
    if (!value) {
      return '';
    }

    // For the typical Dropdown component a string value is expected.
    // However, for the MultiDropdown, the value must be an array of objects.
    let simpleValue;
    const isArray = Array.isArray(value);
    if (isArray) {
      simpleValue = value[0];
    } else {
      simpleValue = value;
    }

    return simpleValue;
  };

  onSuggestionSelected(event, options) {
    if (this.props.onSuggestionSelected) {
      const allAgencies = this.getAgencyList();
      const selectedAgency = allAgencies.find((x) => options?.suggestion?.text === x);
      this.props.onSuggestionSelected(event, options, selectedAgency);
    }
  }

  render() {
    const className = classnames(['agency', 'usa-form-control'], this.props.className);
    const options = this.renderOptions();
    const value = this.appropriateValue(this.props.value);
    return (
      <div>
        {this.state.focus && value && !this.validAgency(value) && (
          <ErrorMessage
            isWarning
            title={this.props.warningMessage}
          />
        )}
        <Dropdown
          attribute={this.props.attribute}
          name={this.props.name}
          ref={this.agency}
          label={this.props.label}
          placeholder={this.props.placeholder}
          className={className}
          ariaLabel={this.props.ariaLabel}
          disabled={this.props.disabled}
          onUpdate={this.updateSingleAgency}
          onError={this.handleError}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onSuggestionSelected={this.onSuggestionSelected}
          value={value}
          required={this.props.required}
          suggestions={options}
          sort={false}
          suggestionsRenderProvided
          dynamicSuggestions
          hasFullLengthOptions
        >
          {options}
        </Dropdown>
      </div>
    );
  }
}

FederalAgencyAutoComplete.defaultProps = {
  name: 'agency',
  value: [],
  focus: false,
  showComments: false,
  warningMessage: 'Please select from the list.',
  updateAgencyFocused: (focus) => { },
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  onSuggestionSelected: (event, options, selectedAgency) => {}
};

FederalAgencyAutoComplete.errors = [];
